import { clsx } from "clsx";
import { motion } from "framer-motion";
import type { ReactNode } from "react";
import { SpinnerIcon } from "../icons/SpinnerIcon";

type ButtonVariant = "primary" | "secondary" | "ghost" | "line";
type ButtonSize = "sm" | "md" | "lg";

type ButtonProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
  children: ReactNode;
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
  onClick?: () => void;
};

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    "bg-ocean-500 text-white shadow-button hover:bg-ocean-600 hover:shadow-button-hover focus-visible:shadow-focus",
  secondary:
    "bg-white text-ocean-700 border border-ocean-200 shadow-card hover:bg-ocean-50 hover:border-ocean-300 focus-visible:shadow-focus",
  ghost:
    "bg-transparent text-ocean-600 hover:bg-ocean-50 hover:text-ocean-700 focus-visible:shadow-focus",
  line: "bg-line text-white shadow-button hover:bg-line-hover hover:shadow-button-hover focus-visible:shadow-focus-line",
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: "px-4 py-2.5 text-sm min-h-[40px]",
  md: "px-5 py-3.5 text-base min-h-[48px]",
  lg: "px-6 py-4 text-lg min-h-[52px]",
};

export const Button = ({
  variant = "primary",
  size = "md",
  isLoading = false,
  leftIcon,
  rightIcon,
  fullWidth = false,
  children,
  disabled,
  type,
  onClick,
}: ButtonProps) => {
  const isDisabled = disabled || isLoading;

  return (
    <motion.button
      whileHover={isDisabled ? undefined : { scale: 1.02 }}
      whileTap={isDisabled ? undefined : { scale: 0.98 }}
      transition={{ type: "spring", stiffness: 400, damping: 17 }}
      className={clsx(
        "relative inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition-all duration-200",
        variantStyles[variant],
        sizeStyles[size],
        fullWidth && "w-full",
        isDisabled && "cursor-not-allowed opacity-50",
      )}
      type={type}
      disabled={isDisabled}
      onClick={onClick}
    >
      {isLoading ? (
        <>
          <SpinnerIcon className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 h-5 w-5 animate-spin" />
          <span className="opacity-0">{children}</span>
        </>
      ) : (
        <>
          {leftIcon && <span className="shrink-0">{leftIcon}</span>}
          {children}
          {rightIcon && <span className="shrink-0">{rightIcon}</span>}
        </>
      )}
    </motion.button>
  );
};
